import * as React from 'react'
import { Box, Button, TextareaAutosize } from '@mui/material'
import {
  allUrlsInTree,
  contentFiles,
  getCurriculaContentFolder,
  isIngestedSource,
  ISubject,
  retrieveIngestedItem,
  sortedUrls,
  subjectForCourse,
  uploadContentFiles,
  uploadCourse,
} from './Utility'
import { CollapsableJson } from 'shared/ui'
import { ICourseNode } from './IngestedTree'
import { countNodeTypes } from '../../ingestion/utility'
import { CountTable } from 'components/utility/CountTable'
import { PartnerContentUploadFromContext } from '../content_upload/ContentUpload'
import { PartnerUploadContext } from 'ingestion/PartnerContext'
import { ConfirmCommitButton } from 'shared/ui'
import { FileSelector } from '../FileSelector'
import { downloadImportUrlMap } from '../../utility'
import CurriculaOverrides, { IOverrides } from './CurriculaOverrides'
import { CreateTreeFromIngestion } from './Ingested'
import { filesThatMeetCondition } from 'shared/utils'
import { AwsContentItem } from 'shared/types/ingestion'

function replaceJsonContent(
  jsonObj: object,
  replaceMap: { [key: string]: string },
) {
  const asString = JSON.stringify(jsonObj)
  const replaced = Object.entries(replaceMap).reduce(
    (acc: string, [toFind, toReplace]) => {
      // this is to protect against swapping twice, maybe there's a better way?
      if (acc.includes(toReplace)) return acc
      return acc.replaceAll(toFind, toReplace)
    },
    asString,
  )
  if (asString === replaced) {
    return jsonObj
  }
  return JSON.parse(replaced)
}

function replaceUrlsWithFilenameMapping(
  jsonObj: object,
  existingUrlsByFileName: { [key: string]: string },
  newUrlsByFileName: { [key: string]: string },
) {
  const replaceMap = Object.entries(newUrlsByFileName).reduce(
    (acc: { [key: string]: string }, [fileName, newUrl]) => {
      if (fileName in existingUrlsByFileName) {
        acc[existingUrlsByFileName[fileName]] = newUrl
      }
      return acc
    },
    {},
  )
  return replaceJsonContent(jsonObj, replaceMap)
}

export function IngestedItem({
  partnerKey,
  ingestionId,
}: {
  partnerKey: string
  ingestionId: string
}) {
  const [thisSubject, setThisSubject] = React.useState<ISubject>()
  const [ingestedTree, setIngestedTree] = React.useState<ICourseNode>()
  const [currentError, setCurrentError] = React.useState<string | undefined>()
  const [hasChanges, setHasChanges] = React.useState(false)
  const [curriculaValues, setCurriculaValues] = React.useState<IOverrides>({})

  React.useEffect(() => {
    retrieveIngestedItem(partnerKey, ingestionId).then(({ tree, error }) => {
      setCurrentError(error?.replace(/(?:\r\n|\r|\n)/g, '<br>'))
      setIngestedTree(tree)
    })
  }, [partnerKey, ingestionId])

  React.useEffect(() => {
    if (!ingestedTree) return
    subjectForCourse(ingestedTree).then(setThisSubject)
  }, [ingestedTree])

  const updateIngestedTree = React.useCallback((updated: ICourseNode) => {
    setHasChanges(true)
    setIngestedTree(updated)
  }, [])

  function uploadNewIngestion() {
    if (!ingestedTree) return
    return uploadCourse(partnerKey, ingestedTree).then(() =>
      setHasChanges(false),
    )
  }

  if (!ingestedTree) return <Box>Loading...</Box>

  return (
    <PartnerUploadContext.Provider
      value={{
        partnerKey,
        subject: curriculaValues.subject,
        gradeCode: curriculaValues.gradeId,
        environment: curriculaValues.environment,
      }}
    >
      <Box>
        <Box padding={'10px'} border={'solid 1px'}>
          <Box paddingBottom={'10px'}>{`${partnerKey}: ${ingestionId}`}</Box>
          <CurriculaOverrides
            ingestion={ingestedTree}
            onChange={setCurriculaValues}
          />
          <Box>
            <Box>
              If you make changes to this ingested tree you can use this button
              to upload a new Ingestion file
              <br />
              You can override where source files are going by using the boxes
              above
            </Box>
            <ConfirmCommitButton
              disabled={!hasChanges}
              onClick={uploadNewIngestion}
            >
              <>Upload New Ingestion</>
            </ConfirmCommitButton>
            <CreateTreeFromIngestion
              ingestionKey={ingestionId}
              partnerKey={partnerKey}
            />
          </Box>
        </Box>
        {currentError && (
          <Box padding={'5px'}>
            <span dangerouslySetInnerHTML={{ __html: currentError }} />
          </Box>
        )}
        <Box padding={'10px'} border={'solid 1px'}>
          <Box>The contents of the ingested tree:</Box>
          <CollapsableJson json={ingestedTree} title={''} collapsed={true} />
        </Box>
        <Box padding={'10px'} border={'solid 1px'}>
          <Box>Ingested nodes by type:</Box>
          <NodeCounts ingestion={ingestedTree} />
        </Box>
        {ingestedTree && (
          <ImageTags
            ingestedTree={ingestedTree}
            onUpdate={updateIngestedTree}
          />
        )}
        {ingestedTree &&
          thisSubject &&
          curriculaValues.gradeId &&
          curriculaValues.environment &&
          curriculaValues.subject && (
            <LocalResourcesReplace
              ingestedTree={ingestedTree}
              partnerKey={partnerKey}
              subject={curriculaValues.subject}
              gradeId={curriculaValues.gradeId}
              environment={curriculaValues.environment}
              onUpdate={updateIngestedTree}
            />
          )}
        <PartnerContentUploadFromContext />
      </Box>
    </PartnerUploadContext.Provider>
  )
}

function skipIngestion(src: string): boolean {
  return isIngestedSource(src) || src.startsWith('data')
}

export function LocalResourcesReplace({
  ingestedTree,
  partnerKey,
  subject,
  gradeId,
  environment,
  onUpdate,
  srcFolder,
}: {
  ingestedTree: ICourseNode
  partnerKey: string
  subject: string
  gradeId: string
  environment: string
  onUpdate(updated: ICourseNode): void
  srcFolder?: any
}) {
  const [alreadyUploaded, setAlreadyUploaded] = React.useState<{
    [name: string]: string
  }>({})
  const [localSrcUrls, setLocalSrcUrls] = React.useState<{
    [name: string]: string
  }>({})
  const [treeLinks, setTreeLinks] = React.useState<{
    [domain: string]: string[]
  }>({})

  React.useEffect(() => {
    getCurriculaContentFolder({
      partnerId: partnerKey,
      currId: subject,
      gradeCode: gradeId,
    }).then((uploaded) => {
      setAlreadyUploaded(uploaded)
    })
  }, [partnerKey, subject, gradeId])

  React.useEffect(() => {
    const urls = allUrlsInTree(ingestedTree)
    // Display these, we need to replace with files
    // detect if selected files have same name, upload and replace
    // for tree
    const { withoutDomain, byDomain } = sortedUrls(urls)
    setTreeLinks(byDomain)
    setLocalSrcUrls(
      withoutDomain.reduce((acc: { [k: string]: string }, file) => {
        const name = file.split('/').pop()
        name && (acc[name] = file)
        return acc
      }, {}),
    )
  }, [ingestedTree])

  React.useEffect(() => {
    const replaced = replaceUrlsWithFilenameMapping(
      ingestedTree,
      localSrcUrls,
      alreadyUploaded,
    )
    onUpdate(replaced)
  }, [alreadyUploaded, ingestedTree, localSrcUrls, onUpdate])

  const localImageImport = React.useCallback(
    (imageFiles: File[], treeLinks: Record<string, string>) => {
      uploadContentFiles(
        {
          partnerKey,
          subject,
          curricula: gradeId,
          envOverride: environment,
        },
        ...imageFiles,
      ).then((ourFiles) => {
        onUpdate(
          replaceUrlsWithFilenameMapping(ingestedTree, treeLinks, ourFiles),
        )
      })
    },
    [partnerKey, ingestedTree, subject, gradeId, environment, onUpdate],
  )
  return (
    <ExternalResourceScript
      localImageImport={localImageImport}
      ingestedTree={ingestedTree}
      srcFolder={srcFolder}
      treeLinks={treeLinks}
      localSrcLinks={localSrcUrls}
    />
  )
}

// This thing gives you a file of curl requests to upload content from local machine
function ExternalResourceScript({
  ingestedTree,
  localImageImport,
  srcFolder,
  treeLinks,
  localSrcLinks,
}: {
  ingestedTree: ICourseNode
  localImageImport: (
    files: File[],
    sourceLinks: Record<string, string>,
  ) => unknown
  srcFolder?: any
  treeLinks: { [domain: string]: string[] }
  localSrcLinks: { [name: string]: string }
}) {
  const { subject, partnerKey, environment } =
    React.useContext(PartnerUploadContext)

  async function onSelect(key: string) {
    if (!subject || !partnerKey) return
    downloadImportUrlMap({
      externalUrls: treeLinks[key],
      partnerKey,
      subject,
      envOverride: environment,
      gradeId: ingestedTree.data.gradeID + '',
    })
  }

  return (
    <Box>
      <LocalSrcLinks
        sourceByName={localSrcLinks}
        importFiles={localImageImport}
        srcFolder={srcFolder}
      />
      <Box padding={'10px'}>
        <Box padding={'5px'}>
          <Box>
            <b>External Asset Import</b>
          </Box>
          This table shows assets that are linked to an external domain, if any
          exist we can retrieve curl requests to use on local machine to pull
          and push to our S3
        </Box>
        <CountTable header1={'Domain'} data={treeLinks} onSelect={onSelect} />
      </Box>
    </Box>
  )
}

function LocalSrcLinks({
  sourceByName,
  importFiles,
  srcFolder,
}: {
  sourceByName: { [k: string]: string }
  importFiles: (files: File[], treeLinks: Record<string, string>) => void
  srcFolder?: any
}) {
  const [selectedFiles, setSelectedFiles] = React.useState<any[]>([])

  const fileFilter = React.useCallback(
    ({ name }: any) => name in sourceByName,
    [sourceByName],
  )

  const doFolder = React.useCallback(
    (maybeFolder?: any) => {
      if (maybeFolder?.isDirectory) {
        filesThatMeetCondition(maybeFolder, fileFilter).then((files) => {
          setSelectedFiles(files)
        })
        return true
      }
      return false
    },
    [fileFilter],
  )

  React.useEffect(() => {
    doFolder(srcFolder)
  }, [srcFolder, doFolder])

  function onFilesSelected(files: File[], [maybeFolder]: any[]) {
    if (doFolder(maybeFolder)) return
    const matches = files.filter(fileFilter)
    setSelectedFiles(matches)
  }

  return (
    <Box margin={'10px'} padding={'5px'} border={'solid 1px'}>
      These assets have local links, we should have the files in the export. You
      have an ingestion here, now you can upload the files for these assets and
      replace these links with links to our S3.
      <Box>
        <Box padding={'10px'}>
          <b>Linked To Local Assets</b>
        </Box>
        {Object.entries(sourceByName).map(([name, link]) => (
          <Box key={name}>{link}</Box>
        ))}
      </Box>
      <Box>
        <Box padding={'5px'}>
          <FileSelector filesSelected={onFilesSelected} />
        </Box>
        <Box>
          <Button
            onClick={() => importFiles(selectedFiles, sourceByName)}
            disabled={!selectedFiles.length}
          >
            Upload And Replace
          </Button>
        </Box>
        {selectedFiles.map((link, i) => (
          <Box key={i}>{link.name}</Box>
        ))}
      </Box>
    </Box>
  )
}

function NodeCounts({ ingestion }: { ingestion: ICourseNode }) {
  const [nodeCounts, setNodeCounts] = React.useState<any>()

  React.useEffect(() => {
    setNodeCounts(countNodeTypes(ingestion))
  }, [ingestion])

  return (
    <Box>
      {nodeCounts && (
        <CollapsableJson json={nodeCounts} title={'Node Counts'} />
      )}
    </Box>
  )
}

// This thing replaces outside links with the kiddom links when available
function ImageTags({
  ingestedTree,
  onUpdate,
}: {
  ingestedTree: ICourseNode
  onUpdate: (updated: ICourseNode) => void
}) {
  const [imgTags, setImgTags] = React.useState<string[]>([])
  const [existingFiles, setExistingFiles] = React.useState<{
    [key: string]: AwsContentItem
  }>({})

  React.useEffect(() => {
    const jsonString = JSON.stringify(ingestedTree)
    const matches = jsonString.matchAll(/<img [^>]*src=\\"([^"]*)\\"[^>]*>/gm)
    let next = matches.next()
    const all: string[] = []
    while (!next.done) {
      const nextSource = next.value[1]
      if (
        nextSource &&
        !skipIngestion(nextSource) &&
        !all.includes(nextSource)
      ) {
        all.push(nextSource)
      }
      next = matches.next()
    }
    setImgTags(all)
  }, [ingestedTree])

  const { subject, gradeCode, partnerKey } =
    React.useContext(PartnerUploadContext)

  React.useEffect(() => {
    if (!subject || !partnerKey || !gradeCode) return
    contentFiles({
      partnerKey,
      subject,
      curricula: gradeCode,
    }).then(setExistingFiles)
  }, [ingestedTree, partnerKey, subject, gradeCode])

  return (
    <Box>
      <Box>Matches</Box>
      <DoAllReplaces
        imgTags={imgTags}
        existing={existingFiles}
        onUpdate={onUpdate}
        ingestedTree={ingestedTree}
      />
    </Box>
  )
}

function DoAllReplaces({
  imgTags,
  existing,
  onUpdate,
  ingestedTree,
}: {
  imgTags: string[]
  existing: {
    [key: string]: AwsContentItem
  }
  onUpdate: (updated: ICourseNode) => void
  ingestedTree: ICourseNode
}) {
  const [canSwap, setCanSwap] = React.useState<string[]>([])
  const [displayValue, setDisplayValue] = React.useState('')
  const [inReview, setInReview] = React.useState(false)

  React.useEffect(() => {
    let toDisplay = ''
    const withMatch = imgTags.reduce((acc: string[], img) => {
      const forReplace = existing[fileName(img)]
      if (forReplace) {
        acc.push(img)
        toDisplay += `${img}\n${forReplace.URL}\n\n`
      }
      return acc
    }, [])
    setCanSwap(withMatch)
    setDisplayValue(toDisplay)
  }, [imgTags, existing])

  function fileName(url: string) {
    return decodeURI(url.split('/').pop() as string)
  }

  function doReplace() {
    let asString = JSON.stringify(ingestedTree)
    canSwap.forEach((url) => {
      const replaceWith = existing[fileName(url)].URL
      asString = asString.replaceAll(url, replaceWith)
    })
    onUpdate(JSON.parse(asString))
  }

  return (
    <Box>
      <Button onClick={doReplace}>Can Swap {canSwap.length}</Button>
      <Button onClick={() => setInReview(!inReview)}>Review</Button>
      {inReview && (
        <TextareaAutosize
          style={{ width: '100%', height: '120px', overflow: 'auto' }}
          value={displayValue}
          disabled={true}
        />
      )}
    </Box>
  )
}
